/**
 * 14g — Work · one list for events and assessments.
 *
 * Follows `design/screens/14g.html`: a search field, counted filter chips, a cohort
 * filter, then one table whose Type column is the only thing separating a lesson from a
 * test. The closing note is the design's own.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../lib/session';
import { useQuery } from '../../lib/useQuery';
import { BandChip, Btn, Chip, DesignShell, Empty, ErrorNote, Loading, S, Table, Td, type Band } from '../../design/kit';

interface Row {
  id: string;
  kind: 'lesson' | 'assessment';
  title: string;
  cohortId: string;
  cohortName: string;
  curriculum: string | null;
  status: 'lobby' | 'live' | 'paused' | 'ended';
  needsMarking: number;
  band: Band | null;
  released: boolean;
  updatedAt: string;
  createdAt: string;
}

type FilterKey = 'all' | 'marking' | 'ready' | 'live' | 'returned' | 'drafts';

/** "Today" / "Yesterday" / "Fri 8 Aug" — the design's own date column. */
function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === -1) return 'Yesterday';
  if (days === 1) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

export function WorkList() {
  const { school } = useSession();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [q, setQ] = useState('');
  const [cohortId, setCohortId] = useState('');
  const { data, loading, error, reload } = useQuery<{ events: Row[] }>('/events');
  const cohorts = useQuery<{ cohorts: { id: string; name: string; grade: string }[] }>('/people/cohorts');

  const rows = data?.events ?? [];

  const counts = useMemo(
    () => ({
      all: rows.length,
      marking: rows.filter((r) => r.needsMarking > 0).length,
      ready: rows.filter((r) => r.status === 'lobby').length,
      live: rows.filter((r) => r.status === 'live').length,
      returned: rows.filter((r) => r.released).length,
      drafts: rows.filter((r) => r.status === 'ended' && !r.released && r.needsMarking === 0).length,
    }),
    [rows],
  );

  const shown = rows
    .filter((r) => {
      if (filter === 'marking') return r.needsMarking > 0;
      if (filter === 'ready') return r.status === 'lobby';
      if (filter === 'live') return r.status === 'live';
      if (filter === 'returned') return r.released;
      if (filter === 'drafts') return r.status === 'ended' && !r.released && r.needsMarking === 0;
      return true;
    })
    .filter((r) => !cohortId || r.cohortId === cohortId)
    .filter((r) => !q || r.title.toLowerCase().includes(q.toLowerCase()) || r.cohortName.toLowerCase().includes(q.toLowerCase()));

  /** One action per row, named for what it actually does next. */
  const actionFor = (r: Row): { label: string; to: string; tone: 'good' | 'warn' | 'brand' | 'plain' } => {
    if (r.status === 'live') return { label: 'Rejoin', to: `/teacher/events/${r.id}/live`, tone: 'good' };
    if (r.status === 'lobby') return { label: 'Start', to: `/teacher/events/${r.id}/lobby`, tone: 'good' };
    if (r.needsMarking > 0) return { label: `Mark ${r.needsMarking}`, to: `/teacher/events/${r.id}/marking`, tone: 'warn' };
    if (r.released) return { label: 'Returned', to: `/teacher/events/${r.id}/record`, tone: 'plain' };
    return { label: 'Release', to: `/teacher/events/${r.id}/release`, tone: 'brand' };
  };

  const FILTERS: { key: FilterKey; label: string; count?: number }[] = [
    { key: 'all', label: 'All' },
    { key: 'marking', label: 'Needs marking', count: counts.marking },
    { key: 'ready', label: 'Ready to teach', count: counts.ready },
    { key: 'live', label: 'Live now', count: counts.live },
    { key: 'returned', label: 'Returned', count: counts.returned },
    { key: 'drafts', label: 'To release', count: counts.drafts },
  ];

  return (
    <DesignShell
      brand={school?.name}
      title="Work"
      actions={
        <>
          <Btn kind="secondary" to="/teacher/import">Import material</Btn>
          <Btn kind="primary" to="/teacher/new-lesson">New</Btn>
        </>
      }
    >
      {/* Search + filters, exactly the design's toolbar. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search everything I teach"
          aria-label="Search everything I teach"
          style={{
            fontSize: '0.8125rem', fontWeight: 500, background: '#fff',
            border: '1px solid rgba(17,20,28,.12)', borderRadius: '9px',
            padding: '10px 14px', minWidth: '240px', color: 'var(--c-ink)',
          }}
        />
        {FILTERS.map((f) => {
          const on = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              aria-pressed={on}
              onClick={() => setFilter(f.key)}
              style={{
                fontSize: '0.7812rem', fontWeight: on ? 600 : 500, cursor: 'pointer',
                color: on ? '#fff' : 'var(--c-ink-3)', background: on ? 'var(--c-ink)' : '#fff',
                border: on ? 'none' : '1px solid rgba(17,20,28,.1)',
                padding: '9px 13px', borderRadius: '8px',
              }}
            >
              {f.label}{f.count !== undefined ? ` ${f.count}` : ''}
            </button>
          );
        })}
        <select
          value={cohortId}
          onChange={(e) => setCohortId(e.target.value)}
          aria-label="Filter by cohort"
          style={{
            fontSize: '0.7812rem', fontWeight: 500, color: 'var(--c-ink-3)', background: '#fff',
            border: '1px solid rgba(17,20,28,.1)', padding: '9px 13px', borderRadius: '8px', cursor: 'pointer', minHeight: '44px',
          }}
        >
          <option value="">All cohorts</option>
          {cohorts.data?.cohorts.map((c) => (
            <option key={c.id} value={c.id}>{c.grade} · {c.name}</option>
          ))}
        </select>
      </div>

      {loading && !data ? (
        <Loading />
      ) : error ? (
        <ErrorNote error={error} retry={reload} />
      ) : shown.length === 0 ? (
        <div style={S.cardFlush}>
          <Empty
            title={q || filter !== 'all' ? 'Nothing matches that' : 'Nothing here yet'}
            body={
              q || filter !== 'all'
                ? 'Try a different filter, or clear the search.'
                : 'A lesson and a test are the same object at different pacing — both show up here once you make one.'
            }
            action={<Btn kind="primary" to="/teacher/new-lesson">New lesson</Btn>}
          />
        </div>
      ) : (
        <Table
          minWidth={880}
          columns={[
            { label: 'When', width: '110px' },
            { label: 'Type', width: '90px' },
            { label: 'Title' },
            { label: 'Cohort', width: '210px' },
            { label: 'Band', width: '130px' },
            { label: 'Status', width: '110px', align: 'right' },
          ]}
        >
          {shown.map((r) => {
            const action = actionFor(r);
            return (
              <tr key={r.id}>
                <Td style={{ whiteSpace: 'nowrap' }}>{whenLabel(r.updatedAt || r.createdAt)}</Td>
                <Td>{r.kind === 'assessment' ? 'Test' : 'Lesson'}</Td>
                <Td style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--c-ink)' }}>{r.title}</Td>
                <Td>{[r.cohortName, r.curriculum].filter(Boolean).join(' · ') || 'not assigned'}</Td>
                <Td>{r.band ? <BandChip band={r.band} /> : <span style={S.muted}>—</span>}</Td>
                <Td align="right">
                  <button
                    type="button"
                    onClick={() => navigate(action.to)}
                    style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    <Chip tone={action.tone}>{action.label}</Chip>
                  </button>
                </Td>
              </tr>
            );
          })}
        </Table>
      )}

      <p style={{ ...S.mutedSm, lineHeight: 1.65, maxWidth: '760px', margin: '4px 0 0' }}>
        A lesson and a test are the same thing at different pacing — one you run, one they sit. Keeping them in one
        list means a teacher has one place to look and one thing to search.
      </p>
    </DesignShell>
  );
}
